import { ApiError, mapFirebaseError } from '../errors/ApiError';
import { trackCloudFunctionCall } from '../../services/cloudFunctionsMonitoringService';

/**
 * executeService: Wraps an operation with consistent error handling and standardizes the thrown error.
 */
export const executeService = async <T>(
    operation: () => Promise<T>,
    operationName: string = 'Operation',
    onError: ((error: ApiError) => void) | null = null
): Promise<T> => {
    try {
        const result = await operation();
        return result;
    } catch (error: any) {
        let standardizedError: ApiError;

        if (error instanceof ApiError) {
            standardizedError = error;
        } else if (error.code && error.message) {
            standardizedError = mapFirebaseError(error);
        } else {
            standardizedError = new ApiError(
                'UNKNOWN_ERROR',
                `${operationName} failed: ${error.message || 'Unknown error'}`,
                error
            );
        }

        if (onError) {
            onError(standardizedError);
        } else {
            console.error(
                `[${standardizedError.code}] ${standardizedError.message}`,
                standardizedError.originalError
            );
        }

        throw standardizedError;
    }
};

/**
 * executeCloudFunction: Wraps a cloud function call with monitoring and standard error handling.
 */
export const executeCloudFunction = async <T>(
    cloudFunctionName: string,
    operation: () => Promise<T>,
    operationName: string = 'CloudFunction',
    onError: ((error: ApiError) => void) | null = null
): Promise<T> => {
    return trackCloudFunctionCall(cloudFunctionName, async () => {
        return executeService(operation, operationName, onError);
    });
};

/**
 * tryCatch: Higher-order function that wraps an async function with standard API error handling.
 */
export const tryCatch = <Args extends any[], R>(fn: (...args: Args) => Promise<R>) => {
    return async (...args: Args): Promise<R> => {
        try {
            return await fn(...args);
        } catch (error: any) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw mapFirebaseError(error);
        }
    };
};
